import { useEffect, useRef } from "react";
import { useDashboard } from "../../hooks/useDashboard";
import { DashboardWrapper, Square, SpanTitle, SpanQuantity, ChartsContainer } from './styles';
import Highcharts from "highcharts/highcharts-gantt";
import HighchartsReact from 'highcharts-react-official';
import { Tooltip } from 'antd';

import * as _ from 'lodash';

export function Dashboard(props: HighchartsReact.Props) {
  const {
    phasesProject,
    phasesWithTasksInProgress,
    projectsNumber,
    finishedProjects,
    projectsByGroups,
    forecastFinish,
    lateTasks,
    fetchLateTasks,
    fetchPhasesProject,
    dataPercentageToCompleteProjects,
    percentageToCompleteProjects,
    userProjectAndTasksNotComplete,
    dataUserProjectAndTasksNotComplete,
  } = useDashboard();

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const fetchDashboardData = _.debounce(() => {
    fetchPhasesProject();
    fetchLateTasks();
    percentageToCompleteProjects();
    userProjectAndTasksNotComplete();
  }, 500);


  useEffect(() => {
    fetchDashboardData();
  }, []);

  const seriesUsers = [
    {
      name: "Projetos Não Finalizados",
      data: dataUserProjectAndTasksNotComplete.map((dataUser) => dataUser.projectsNotComplete),
    },
    {
      name: "Tarefas Não Finalizadas",
      data: dataUserProjectAndTasksNotComplete.map((dataUser) => dataUser.tasksNotComplete),
    }
  ];

  const optionsProjectNotComplete = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Percentual de Conclusão das Obras',
      align: 'left',
    },
    xAxis: {
      categories: dataPercentageToCompleteProjects.map((data) => data.projectName),
      crosshair: true,
      accessibility: {
        description: 'Projetos',
      },
    },
    legend: {
      enabled: false
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Porcentagem',
      },
    },
    tooltip: {
      formatter(): any {
        // @ts-ignore
        return '<b>' + this.x + '</b>: ' + this.y + '%';
      },
    },
    series: [{
      data: dataPercentageToCompleteProjects.map((data) => ({
        name: data.projectName,
        y: data.percentageToComplete
      }))
    }]
  };

  const optionsUserProjectsAndTasksNotComplete = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Projetos e Tarefas Não Finalizados por Usuário',
      align: 'left',
    },
    xAxis: {
      categories: dataUserProjectAndTasksNotComplete.map((dataUser) => dataUser.userName),
      crosshair: true,
      accessibility: {
        description: 'Projetos',
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Quantidade',
      },
    },
    series: seriesUsers
  };

  const optionsProjectsPie = {
    chart: {
      type: 'pie',
    },
    title: {
      text: 'Total de Projetos',
      align: 'left',
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b>'
    },
    accessibility: {
      point: {
        valueSuffix: ''
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.y}'
        }
      }
    },
    series: [{
      name: 'Projetos',
      colorByPoint: true,
      data: [
        {
          name: 'Projetos',
          y: projectsNumber || 0,
        }
      ]
    }]
  };

  const optionsProjectsByGroups = {
    chart: {
      type: 'pie',
    },
    title: {
      text: 'Projetos por Grupo',
      align: 'left',
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b>'
    },
    accessibility: {
      point: {
        valueSuffix: ''
      }
    },
    series: [{
      name: 'Projetos',
      colorByPoint: true,
      data: projectsByGroups.map((group) => ({
        name: group.title || 'Sem Grupo',
        y: parseInt(group.quantity)
      }))
    }]
  };

  /* Projetos por fase - phasesProject */
  const optionsPhasesProject = {
    chart: {
      type: 'pie',
    },
    title: {
      text: 'Projetos por Fase',
      align: 'left',
    },
    xAxis: {
      categories: phasesWithTasksInProgress.map((phase) => phase.title),
      crosshair: true,
      accessibility: {
        description: 'Fases',
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Quantidade',
      },
    },
    series: [{
      name: 'Projetos',
      data: phasesWithTasksInProgress.map((phase) => ({
        name: phase.title,
        y: parseInt(phase.tasksInProgress)
      })),
    }]
  };

  function formatMonthYear(monthYear: string) {
    const [year, month] = monthYear.split('-');
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const idx = parseInt(month, 10) - 1;
    return `${meses[idx]}/${year}`;
  }

  const optionsForecastFinish = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Previsão de término',
      align: 'left',
    },
    xAxis: {
      categories: forecastFinish?.map(item => formatMonthYear(item.month)),
      title: {
        text: 'Mês',
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Total de tarefas',
      },
      allowDecimals: false,
    },
    tooltip: {
      pointFormat: 'Total: <b>{point.y}</b>',
    },
    series: [{
      name: 'Tarefas',
      data: forecastFinish?.map(item => item.total),
      color: '#1890ff'
    }]
  };

  return (
    <div>
      <DashboardWrapper>
        <Square key="projectsNumber">
          <SpanTitle>Total de Projetos</SpanTitle>
          <SpanQuantity>{projectsNumber}</SpanQuantity>
        </Square>

        <Square key="finishedProjects">
          <SpanTitle>Entregue</SpanTitle>
          <SpanQuantity>{finishedProjects}</SpanQuantity>
        </Square>

        <Square key="finishedProjects">
          <SpanTitle>Tasks Atrasada</SpanTitle>
          <SpanQuantity>{lateTasks}</SpanQuantity>
        </Square>
        {phasesProject.map((item, index) => (
          <Square key={index} >
            <Tooltip title={item.title} placement="top">
              <SpanTitle>{item.title}</SpanTitle>
            </Tooltip>
            <SpanQuantity>{item.quantity}</SpanQuantity>
          </Square>
        ))}
      </DashboardWrapper>

      <ChartsContainer className="charts-container">
        <HighchartsReact
          highcharts={Highcharts}
          options={optionsProjectNotComplete}
          ref={chartComponentRef}
          {...props}
        />

        <HighchartsReact
          highcharts={Highcharts}
          options={optionsUserProjectsAndTasksNotComplete}
          ref={chartComponentRef}
          {...props}
        />
      </ChartsContainer>

      <ChartsContainer>
        <HighchartsReact
          highcharts={Highcharts}
          options={optionsProjectsPie}
        />
        <HighchartsReact
          highcharts={Highcharts}
          options={optionsProjectsByGroups}
        />
        <HighchartsReact
          highcharts={Highcharts}
          options={optionsPhasesProject}
        />
      </ChartsContainer>

      <HighchartsReact
        highcharts={Highcharts}
        options={optionsForecastFinish}
      />
      <ChartsContainer>
      </ChartsContainer>
    </div>
  );
}